import { ChangeDetectionStrategy, Component, computed, ElementRef, input, output, signal, viewChild } from '@angular/core';
import { TarifOption } from '@mwe/models';
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import { NgClass } from '@angular/common';
import ShortUniqueId from 'short-unique-id';

@Component({
  selector: 'mwe-tariff-option-dropdown',
  templateUrl: './tariff-option-dropdown.component.html',
  imports: [NgbDropdown, NgbDropdownToggle, NgClass, NgbDropdownMenu, NgbDropdownItem],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TariffOptionDropdownComponent {
  readonly options = input<TarifOption[]>();
  readonly selectedTariffOptionId = input<string>();
  readonly ariaLabelledBy = input<string>(null);
  readonly isReadonly = input<boolean>();
  readonly selectedTariffGroupId = input<string>(null);
  readonly selectedTariffKey = input<string>(null);

  readonly optionSelected = output<string>();

  private readonly listboxElement = viewChild<ElementRef<HTMLElement>>('listbox');

  private readonly _selectedTariffOptionId = signal<string>('');
  private readonly _componentId = signal<string>(new ShortUniqueId({ dictionary: 'alpha' }).rnd(8));
  readonly selectedOptionIndex = signal<number>(null);

  readonly dropdownId = computed<string>(() => `${this._componentId()}_dropdown`);
  readonly selectedTariffOption = computed<TarifOption>(() => this.options().find(o => this.selectedTariffOptionId() === o.option));
  readonly computedSelectedTariffOptionId = computed<string>(() => this._selectedTariffOptionId() || this.selectedTariffOptionId());
  readonly computedDataFocusKey = computed<string>(() => `${this.selectedTariffKey()}-${this.selectedTariffGroupId()}`);
  private readonly selectedOption = computed<HTMLElement>(() => {
    if (this.selectedOptionIndex() === null) return null;
    return this.listboxElement()?.nativeElement?.querySelectorAll('.dropdown-item')?.item(this.selectedOptionIndex()) as HTMLElement;
  });
  readonly colorCss = computed<string>(() => {
    switch (this.selectedTariffOption()?.farbe) {
      case 'green':
        return 'success';
      case 'blue':
        return 'secondary-light';
      default:
        return 'success';
    }
  });

  keyboardNavigation($event: KeyboardEvent): void {
    if (!this.options().length || !this.selectedOption() || $event.key !== 'Enter') {
      return;
    }

    this.onOptionSelected(this.options()[this.selectedOption().getAttribute('data-suggestion-index')]);
  }

  onOptionSelected(option: TarifOption): void {
    this._selectedTariffOptionId.set(option.option);
    this.optionSelected.emit(this.computedSelectedTariffOptionId());
  }

  showDeltaUnit(option: TarifOption): boolean {
    return !!option?.delta?.trim()?.length && !!option?.deltaUnit?.trim()?.length;
  }

  getOptionId(idx: string): string {
    if (idx === null) return null;
    return `${this._componentId()}_dropdown-option-${idx}`;
  }
}
